import React, { useState, useEffect } from 'react';
import { useWorkspace } from '../../contexts/WorkspaceContext';
import { getAuthHeaders } from '../../services/api/authUtils';

const TokenStatusIndicator = ({ provider = 'netsuite' }) => {
  const { currentWorkspace } = useWorkspace();
  const [tokenStatus, setTokenStatus] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [lastChecked, setLastChecked] = useState(null);
  const [error, setError] = useState(null);

  // API configuration
  const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

  const fetchTokenStatus = async () => {
    if (!currentWorkspace?.id) {
      return;
    }
    
    try {
      const url = `${API_BASE_URL}/api/${provider}/token/status`;
      
      const headers = getAuthHeaders();
      
      const response = await fetch(url, { headers });
      
      
      if (response.ok) {
        const data = await response.json();
        setTokenStatus(data.status);
        setLastChecked(new Date());
        setError(null);
      } else {
        const errorText = await response.text();
        setError(`HTTP ${response.status}: ${response.statusText}`);
      }
    } catch (error) {
      setError(error.message);
    }
  };

  const refreshToken = async () => {
    if (!currentWorkspace?.id || isRefreshing) return;
    
    setIsRefreshing(true);
    try {
      const url = `${API_BASE_URL}/api/${provider}/token/refresh`;
      
      const headers = getAuthHeaders();
      
      const response = await fetch(url, {
        method: 'POST',
        headers
      });
      
      
      const data = await response.json();
      
      if (response.ok) {
        // Refresh the status after successful refresh
        await fetchTokenStatus();
        alert('Token refreshed successfully!');
      } else if (data.requiresReconfiguration || data.requiresReauth) {
        alert('NetSuite M2M credentials require attention. Please reconfigure the NetSuite connection.');
      } else {
        alert(`Token refresh failed: ${data.error || 'Unknown error'}`);
      }
    } catch (error) {
      alert('Failed to refresh token. Please try again.');
    } finally {
      setIsRefreshing(false);
    }
  };

  useEffect(() => {
    fetchTokenStatus();
    // Check token status every 5 minutes
    const interval = setInterval(fetchTokenStatus, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [currentWorkspace?.id]);

  if (error) {
    return (
      <div className="token-status-indicator error">
        <span className="status-dot red"></span>
        <span>Error: {error}</span>
        <button 
          className="refresh-btn"
          onClick={fetchTokenStatus}
          style={{ marginLeft: '10px' }}
        >
          Retry
        </button>
      </div>
    );
  }

  if (!tokenStatus) {
    return (
      <div className="token-status-indicator loading">
        <span className="status-dot gray"></span>
        <span>Checking token status...</span>
        {currentWorkspace ? (
          <span style={{ fontSize: '11px', color: '#666', marginLeft: '10px' }}>
            (Workspace: {currentWorkspace.name})
          </span>
        ) : (
          <span style={{ fontSize: '11px', color: '#666', marginLeft: '10px' }}>
            (No workspace selected)
          </span>
        )}
      </div>
    );
  }

  if (!tokenStatus.configured) {
    return (
      <div className="token-status-indicator not-configured">
        <span className="status-dot red"></span>
        <span>NetSuite M2M not configured</span>
      </div>
    );
  }

  const getStatusInfo = () => {
    if (tokenStatus.isExpired) {
      return { color: 'red', text: 'Token expired', canRefresh: true };
    }
    if (tokenStatus.isNearExpiry) {
      return { color: 'yellow', text: 'Token expires soon', canRefresh: true };
    }
    return { color: 'green', text: 'Token valid', canRefresh: false };
  };

  const statusInfo = getStatusInfo();
  const expiresAt = tokenStatus.expiresAt ? new Date(tokenStatus.expiresAt) : null;

  return (
    <div className="token-status-indicator">
      <div className="status-main">
        <span className={`status-dot ${statusInfo.color}`}></span>
        <span className="status-text">{statusInfo.text}</span>
        {statusInfo.canRefresh && (
          <button 
            className="refresh-btn"
            onClick={refreshToken}
            disabled={isRefreshing}
          >
            {isRefreshing ? 'Minting...' : 'Mint token'}
          </button>
        )}
      </div>
      
      <div className="token-details">
        {expiresAt && (
          <div className="expiry-time">
            Expires: {expiresAt.toLocaleString()}
          </div>
        )}
        {tokenStatus.lastRefreshed && (
          <div className="last-refreshed">
            Last refreshed: {new Date(tokenStatus.lastRefreshed).toLocaleString()}
          </div>
        )}
        {lastChecked && (
          <div className="last-checked">
            Status checked: {lastChecked.toLocaleString()}
          </div>
        )}
      </div>

      <style jsx>{`
        .token-status-indicator {
          padding: 10px;
          border: 1px solid #ddd;
          border-radius: 4px;
          margin: 10px 0;
          background: #f9f9f9;
        }

        .status-main {
          display: flex;
          align-items: center;
          gap: 8px;
          margin-bottom: 8px;
        }

        .status-dot {
          width: 10px;
          height: 10px;
          border-radius: 50%;
          display: inline-block;
        }

        .status-dot.green {
          background-color: #28a745;
        }

        .status-dot.yellow {
          background-color: #ffc107;
        }

        .status-dot.red {
          background-color: #dc3545;
        }

        .status-dot.gray {
          background-color: #6c757d;
        }

        .status-text {
          font-weight: 500;
        }

        .refresh-btn {
          background: #007bff;
          color: white;
          border: none;
          padding: 4px 8px;
          border-radius: 3px;
          cursor: pointer;
          font-size: 12px;
        }

        .refresh-btn:hover:not(:disabled) {
          background: #0056b3;
        }

        .refresh-btn:disabled {
          background: #6c757d;
          cursor: not-allowed;
        }

        .token-details {
          font-size: 12px;
          color: #666;
        }

        .token-details > div {
          margin: 2px 0;
        }

        .loading .status-text {
          color: #6c757d;
        }

        .not-configured .status-text {
          color: #dc3545;
        }
      `}</style>
    </div>
  );
};

export default TokenStatusIndicator;
